import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DIFICULTADES, MEDIOS, chipsRequisitos, medioLabel, dificultadLabel, ventanaTexto } from "@/lib/requisitosMontaje";

const TONE = { amber: "bg-amber-500/15 text-amber-200 border-amber-500/40", rose: "bg-rose-500/15 text-rose-200 border-rose-500/40", cyan: "bg-cyan-500/15 text-cyan-200 border-cyan-500/40", zinc: "bg-zinc-800/70 text-zinc-300 border-zinc-700" };

/** Chips compactos de requisitos (tarjetas / filas). */
export function RequisitosChips({ req, testId = "req-chips", className = "" }) {
  const chips = chipsRequisitos(req);
  if (!chips.length) return null;
  return (
    <span className={`inline-flex flex-wrap gap-1 ${className}`} data-testid={testId}>
      {chips.map((c) => <span key={c.k} className={`text-[10px] px-1 rounded border leading-4 ${TONE[c.tone]}`} data-testid={`${testId}-${c.k}`}>{c.txt}</span>)}
    </span>
  );
}

/** Bloque de lectura completo (ficha lateral, Mi Ruta, hover). */
export function RequisitosDetalle({ req, compacto = false, testId = "req-detalle" }) {
  if (!req) return null;
  const filas = [
    req.material_habitual && ["Material habitual", req.material_habitual],
    req.personas_minimas && ["Personas mínimas", `${req.personas_minimas}`],
    req.dificultad && ["Dificultad", dificultadLabel(req.dificultad)],
    req.medio_acceso && req.medio_acceso !== "ninguno" && ["Medio de acceso", medioLabel(req.medio_acceso, req.medio_acceso_otro)],
    ventanaTexto(req.ventana_horaria) && ["Horario / restricción", `${ventanaTexto(req.ventana_horaria)}${req.ventana_horaria?.texto && (req.ventana_horaria.desde || req.ventana_horaria.hasta) ? ` · ${req.ventana_horaria.texto}` : ""}`],
    req.tiempo_estimado_min && ["Tiempo estimado", `${req.tiempo_estimado_min} min`],
  ].filter(Boolean);
  return (
    <div className="text-xs space-y-1.5" data-testid={testId}>
      {filas.length > 0 && (
        <div className={`grid ${compacto ? "grid-cols-1" : "grid-cols-2"} gap-x-3 gap-y-1`}>
          {filas.map(([k, v]) => <div key={k}><span className="text-[10px] uppercase text-zinc-500">{k}</span><div className="text-zinc-200">{v}</div></div>)}
        </div>
      )}
      {req.instrucciones_montaje && <div className={compacto ? "text-zinc-300 line-clamp-2" : "text-zinc-300 whitespace-pre-wrap bg-zinc-900/60 rounded p-2"}><span className="text-[10px] uppercase text-zinc-500 block">Instrucciones</span>{req.instrucciones_montaje}</div>}
      {req.precauciones && <div className={compacto ? "text-rose-200 line-clamp-2" : "text-rose-200 whitespace-pre-wrap bg-rose-500/5 border border-rose-500/20 rounded p-2"}><span className="text-[10px] uppercase text-rose-400/80 block">Precauciones</span>{req.precauciones}</div>}
    </div>
  );
}

const sel = "h-8 text-xs bg-zinc-900 border border-zinc-800 text-zinc-200 rounded px-2 w-full";

/** Formulario compacto de requisitos (estado en forma de `aFormRequisitos`). Reutilizado en zona (maestro) y en línea (snapshot). */
export default function RequisitosForm({ value, onChange, testId = "req-form", materialLabel = "Material habitual" }) {
  const r = value;
  const set = (patch) => onChange({ ...r, ...patch });
  const setVH = (patch) => onChange({ ...r, ventana_horaria: { ...(r.ventana_horaria || {}), ...patch } });
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" data-testid={testId}>
      <div className="col-span-2">
        <Label className="text-[10px] text-zinc-500">{materialLabel} <span className="text-zinc-600">(sugerencia, no obliga)</span></Label>
        <Input value={r.material_habitual} onChange={(e) => set({ material_habitual: e.target.value })} placeholder="ej. Duratrans, lona, vinilo…" data-testid={`${testId}-material`} className="bg-zinc-900 border-zinc-800 h-8 text-xs" />
      </div>
      <div>
        <Label className="text-[10px] text-zinc-500">Personas mínimas</Label>
        <Input type="number" min="1" max="20" value={r.personas_minimas} onChange={(e) => set({ personas_minimas: e.target.value })} placeholder="1" data-testid={`${testId}-personas`} className="bg-zinc-900 border-zinc-800 h-8 text-xs" />
      </div>
      <div>
        <Label className="text-[10px] text-zinc-500">Dificultad</Label>
        <select value={r.dificultad} onChange={(e) => set({ dificultad: e.target.value })} data-testid={`${testId}-dificultad`} className={sel}>
          <option value="">—</option>{DIFICULTADES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
        </select>
      </div>
      <div>
        <Label className="text-[10px] text-zinc-500">Medio de acceso</Label>
        <select value={r.medio_acceso} onChange={(e) => set({ medio_acceso: e.target.value })} data-testid={`${testId}-medio`} className={sel}>
          <option value="">—</option>{MEDIOS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </div>
      {r.medio_acceso === "otro" && (
        <div>
          <Label className="text-[10px] text-zinc-500">¿Qué medio?</Label>
          <Input value={r.medio_acceso_otro} onChange={(e) => set({ medio_acceso_otro: e.target.value })} data-testid={`${testId}-medio-otro`} className="bg-zinc-900 border-zinc-800 h-8 text-xs" />
        </div>
      )}
      <div>
        <Label className="text-[10px] text-zinc-500">Ventana desde</Label>
        <Input type="time" value={r.ventana_horaria?.desde || ""} onChange={(e) => setVH({ desde: e.target.value })} data-testid={`${testId}-desde`} className="bg-zinc-900 border-zinc-800 h-8 text-xs" />
      </div>
      <div>
        <Label className="text-[10px] text-zinc-500">hasta</Label>
        <Input type="time" value={r.ventana_horaria?.hasta || ""} onChange={(e) => setVH({ hasta: e.target.value })} data-testid={`${testId}-hasta`} className="bg-zinc-900 border-zinc-800 h-8 text-xs" />
      </div>
      <div className={r.medio_acceso === "otro" ? "col-span-2 md:col-span-1" : "col-span-2"}>
        <Label className="text-[10px] text-zinc-500">Restricción horaria (texto)</Label>
        <Input value={r.ventana_horaria?.texto || ""} onChange={(e) => setVH({ texto: e.target.value })} placeholder="ej. antes de apertura" data-testid={`${testId}-horario-texto`} className="bg-zinc-900 border-zinc-800 h-8 text-xs" />
      </div>
      <div>
        <Label className="text-[10px] text-zinc-500">Tiempo est. (min)</Label>
        <Input type="number" min="0" value={r.tiempo_estimado_min} onChange={(e) => set({ tiempo_estimado_min: e.target.value })} data-testid={`${testId}-tiempo`} className="bg-zinc-900 border-zinc-800 h-8 text-xs" />
      </div>
      <div className="col-span-2 md:col-span-4">
        <Label className="text-[10px] text-zinc-500">Instrucciones de montaje</Label>
        <Textarea rows={2} value={r.instrucciones_montaje} onChange={(e) => set({ instrucciones_montaje: e.target.value })} placeholder="ej. Acceso desde interior. Retirar la estantería pesada con cuidado (llena de producto)." data-testid={`${testId}-instrucciones`} className="bg-zinc-900 border-zinc-800 text-xs min-h-[48px]" />
      </div>
      <div className="col-span-2 md:col-span-4">
        <Label className="text-[10px] text-zinc-500">Precauciones</Label>
        <Textarea rows={2} value={r.precauciones} onChange={(e) => set({ precauciones: e.target.value })} placeholder="ej. Riesgo de bisagras al mover la estantería." data-testid={`${testId}-precauciones`} className="bg-zinc-900 border-zinc-800 text-xs min-h-[40px]" />
      </div>
    </div>
  );
}
